import React from 'react';
import {
  DocumentTextIcon,
  MegaphoneIcon,
  PhotoIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { Menu } from '@headlessui/react';
import { Select, SwitchToggle } from '../../../../ui/atoms';
import SelectableCardGroup from '../../../../ui/molecules/SelectableCardGroup';

/**
 * Step 1 (setup): article structure, image settings and advanced setup.
 * Extracted verbatim from pages/Articles/CreateArticle.js.
 */
export default function SetupStep({
  articleType,
  availableStructures,
  complexity,
  complexityOptions,
  contentType,
  onContentTypeChange,
  includeImages,
  language,
  languageOptions,
  selectedStructure,
  setComplexity,
  setIncludeImages,
  setLanguage,
  setSelectedStructure,
  setShowAdvancedSettings,
  setThumbnailSource,
  setToneOfVoice,
  showAdvancedSettings,
  thumbnailSource,
  toneOfVoice,
  toneOfVoiceOptions,
}) {
  const { t } = useTranslation();

  return (
    <div className="mb-8" id="project-selection">
      {/* Content Setup Section */}
      <section id="content-setup" className="mt-0">
        {/* Summary is reached through its own entry point, so only article/changelog are offered here. */}
        {articleType === 'single' && contentType !== 'summary' && onContentTypeChange && (
          <div className="mb-8">
            <h2 className="text-xl font-medium mb-2">{t('articles.create.contentType.title')}</h2>
            <p className="text-base text-gray-500 mb-4">{t('articles.create.contentType.description')}</p>
            <SelectableCardGroup
              options={[
                {
                  id: 'blogpost',
                  title: t('articles.create.contentType.blogpost.title'),
                  icon: DocumentTextIcon,
                  description: t('articles.create.contentType.blogpost.description')
                },
                {
                  id: 'changelog',
                  title: t('articles.create.contentType.changelog.title'),
                  icon: MegaphoneIcon,
                  description: t('articles.create.contentType.changelog.description')
                }
              ]}
              selectedId={contentType}
              onSelect={onContentTypeChange}
              columns={2}
            />
          </div>
        )}

        {articleType === 'single' && (
          <div className="mb-8">
            <h2 className="text-xl font-medium mb-4">{t('articles.create.articleStructure.title')}</h2>

            <Select
              id="article-structure"
              value={selectedStructure ? String(selectedStructure) : ''}
              onChange={(e) => {
                const structureId = e.target.value ? parseInt(e.target.value, 10) : null;
                setSelectedStructure(Number.isNaN(structureId) ? null : structureId);
              }}
            >
              <option value="">{t('articles.create.articleStructure.defaultOption')}</option>
              {availableStructures.map((structure) => (
                <option key={structure.id} value={String(structure.id)}>
                  {structure.title}
                </option>
              ))}
            </Select>

            {selectedStructure && (
              <p className="mt-2 text-sm text-primary-600">
                ✓ {t('articles.create.articleStructure.customSelected')}
              </p>
            )}
          </div>
        )}

        {/* Image Settings */}
        {contentType !== 'short ecommerce product description' && (
          <div className="mt-6">
            <div className="border-t border-gray-200 pt-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-medium mb-4">
                    {t('articles.create.imageSettings.includeImages')}
                  </h2>
                  <p className="text-base text-gray-500">
                    {contentType === 'summary'
                      ? t('articles.create.imageSettings.summaryDescription')
                      : t('articles.create.imageSettings.description')
                    }
                  </p>
                </div>
                <SwitchToggle
                  checked={includeImages}
                  onChange={setIncludeImages}
                  label={t('articles.create.imageSettings.includeImagesToggle')}
                />
              </div>

              {/* Thumbnail Source Selection - only when images enabled */}
              {includeImages && (
                <div className="mt-4">
                  <SelectableCardGroup
                    options={contentType === 'summary'
                      ? [
                          {
                            id: 'pexels',
                            title: t('articles.create.imageSettings.sources.stock.title'),
                            icon: PhotoIcon,
                            description: t('articles.create.imageSettings.sources.stock.description')
                          },
                          {
                            id: 'gemini',
                            title: t('articles.create.imageSettings.sources.ai.title'),
                            icon: SparklesIcon,
                            description: t('articles.create.imageSettings.sources.ai.summaryDescription')
                          }
                        ]
                      : [
                          {
                            id: 'pexels',
                            title: t('articles.create.imageSettings.sources.stock.title'),
                            icon: PhotoIcon,
                            description: t('articles.create.imageSettings.sources.stock.description')
                          },
                          {
                            id: 'gemini',
                            title: t('articles.create.imageSettings.sources.ai.title'),
                            icon: SparklesIcon,
                            description: t('articles.create.imageSettings.sources.ai.description')
                          }
                        ]
                    }
                    selectedId={thumbnailSource}
                    onSelect={setThumbnailSource}
                    columns={2}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Advanced Setup */}
        <section id="audience-setup" className="mb-0 pt-5 pb-2 mt-2 border-t border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-medium mb-1">{t('articles.create.advancedSetup.title')}</h2>
              <p className="text-base text-gray-500 mb-4">
                {t('articles.create.advancedSetup.description')}
              </p>
            </div>
            <SwitchToggle
              checked={showAdvancedSettings}
              onChange={setShowAdvancedSettings}
              label={t('articles.create.advancedSetup.enableToggle')}
            />
          </div>

          {showAdvancedSettings && (
            <div className="space-y-6">
              {/* Tone of Voice */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('articles.create.advancedSetup.toneOfVoice')}</label>
                <Menu as="div" className="relative inline-block w-full text-left">
                  <Menu.Button className="form-dropdown-trigger">
                    {toneOfVoiceOptions.find(opt => opt.value === toneOfVoice)?.label || t('articles.create.advancedSetup.selectPlaceholder')}
                  </Menu.Button>
                  <Menu.Items className="absolute z-10 mt-1 w-full origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    {toneOfVoiceOptions.map(option => (
                      <Menu.Item key={option.value}>
                        {({ active }) => (
                          <button
                            type="button"
                            className={`$ {active ? 'bg-primary-100 text-primary-900' : 'text-gray-900'} group flex w-full items-center px-4 py-2 text-sm`}
                            onClick={() => setToneOfVoice(option.value)}
                          >
                            {option.label}
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                  </Menu.Items>
                </Menu>
              </div>

              {/* Complexity */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('articles.create.advancedSetup.complexity')}</label>
                <Menu as="div" className="relative inline-block w-full text-left">
                  <Menu.Button className="form-dropdown-trigger">
                    {complexityOptions.find(opt => opt.value === complexity)?.label || t('articles.create.advancedSetup.selectPlaceholder')}
                  </Menu.Button>
                  <Menu.Items className="absolute z-10 mt-1 w-full origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    {complexityOptions.map(option => (
                      <Menu.Item key={option.value}>
                        {({ active }) => (
                          <button
                            type="button"
                            className={`$ {active ? 'bg-primary-100 text-primary-900' : 'text-gray-900'} group flex w-full items-center px-4 py-2 text-sm`}
                            onClick={() => setComplexity(option.value)}
                          >
                            {option.label}
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                  </Menu.Items>
                </Menu>
              </div>

              {/* Language */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('articles.create.advancedSetup.language')}</label>
                <Menu as="div" className="relative inline-block w-full text-left">
                  <Menu.Button className="form-dropdown-trigger">
                    {languageOptions.find(opt => opt.value === language)?.label || t('articles.create.advancedSetup.selectPlaceholder')}
                  </Menu.Button>
                  <Menu.Items className="absolute z-10 mt-1 w-full origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    {languageOptions.map(option => (
                      <Menu.Item key={option.value}>
                        {({ active }) => (
                          <button
                            type="button"
                            className={`$ {active ? 'bg-primary-100 text-primary-900' : 'text-gray-900'} group flex w-full items-center px-4 py-2 text-sm`}
                            onClick={() => setLanguage(option.value)}
                          >
                            {option.label}
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                  </Menu.Items>
                </Menu>
              </div>
            </div>
          )}
        </section>
      </section>
    </div>
  );
}
